import React from 'react';
import { DocumentRecord, DocumentCategory } from '../types';
import { TablePagination, useTablePagination } from '../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../shared/useTableSort';

interface Props {
  documents: DocumentRecord[];
  categories: DocumentCategory[];
  onSelect: (doc: DocumentRecord) => void;
  selectedDocId?: string;
}

export const DocumentsTable: React.FC<Props> = ({
  documents,
  onSelect,
  selectedDocId,
}) => {
  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(documents);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);
  const getStatusBadge = (status: string, isOverdue?: boolean) => {
    if (isOverdue) {
      return (
        <span className="badge-pill badge-danger">
          Review Overdue
        </span>
      );
    }

    switch (status) {
      case 'PUBLISHED':
        return (
          <span className="badge-pill badge-success">
            Published
          </span>
        );
      case 'PENDING_APPROVAL':
        return (
          <span className="badge-pill badge-warning">
            Pending Approval
          </span>
        );
      case 'PENDING_REVIEW':
      case 'REVIEW_IN_PROGRESS':
        return (
          <span className="badge-pill badge-info">
            In Review
          </span>
        );
      case 'CHANGES_REQUIRED':
        return (
          <span className="badge-pill badge-danger">
            Changes Required
          </span>
        );
      case 'APPROVED':
        return (
          <span className="badge-pill badge-info">
            Approved
          </span>
        );
      case 'SUPERSEDED':
        return (
          <span className="badge-pill badge-neutral">
            Superseded
          </span>
        );
      default:
        return (
          <span className="badge-pill badge-neutral">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            <SortableTh field="documentNumber" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
              Document No
            </SortableTh>
            <SortableTh field="title" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
              Title & Category
            </SortableTh>
            <SortableTh field="department" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
              Department
            </SortableTh>
            <SortableTh field="version" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
              Version
            </SortableTh>
            <SortableTh field="ownerName" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
              Owner / Controller
            </SortableTh>
            <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
              Status
            </SortableTh>
            <th style={{ textAlign: 'right' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {documents.length === 0 ? (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                No controlled documents found.
              </td>
            </tr>
          ) : (
            paginatedItems.map((doc) => {
              const isSelected = selectedDocId === doc.id;
              return (
                <tr
                  key={doc.id}
                  onClick={() => onSelect(doc)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#eff6ff' : undefined,
                  }}
                >
                  <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>
                    {doc.document_number}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{doc.title}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{doc.category_name || doc.category_id}</div>
                  </td>
                  <td>{doc.department}</td>
                  <td>
                    <span style={{ fontFamily: 'monospace', fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: '#f1f5f9', fontWeight: 600 }}>
                      v{doc.current_version_number || '1.0'}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, color: '#1e293b' }}>{doc.owner_name}</div>
                    {doc.controller_name && (
                      <div style={{ fontSize: '11px', color: '#64748b' }}>Reviewer: {doc.controller_name}</div>
                    )}
                  </td>
                  <td>
                    {getStatusBadge(doc.status, doc.is_review_overdue)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(doc);
                      }}
                      className="btn-secondary"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="documents"
      />
    </div>
  );
};
